import React, { useRef, useState, useEffect, useCallback } from "react";
import { Ban, ChevronLeft, ChevronRight, CheckCircle2, RotateCcw, Circle } from "lucide-react";
import { Button } from "@/components/ui/Button";

const STATUS_CONFIG = {
  APPROVED: {
    icon: CheckCircle2,
    iconClass: "text-tone-emerald",
    badge: "bg-tone-emerald-soft text-tone-emerald",
    active: "border-tone-emerald bg-tone-emerald-soft/40",
  },

  RE_REQUESTED: {
    icon: RotateCcw,
    iconClass: "text-tone-violet",
    badge: "bg-tone-violet-soft text-tone-violet",
    active: "border-tone-violet bg-tone-violet-soft/40",
  },

  NOT_UPLOADED: {
    icon: Circle,
    iconClass: "text-tone-slate",
    badge: "bg-tone-slate-soft text-tone-slate",
    active: "border-tone-slate bg-tone-slate-soft/40",
  },

  REJECTED: {
    icon: Ban,
    iconClass: "text-tone-rose",
    badge: "bg-tone-rose-soft text-tone-rose",
    active: "border-tone-rose bg-tone-rose-soft/40",
  },
};

export const SectionTabs = ({ documents = [], activeDocument, onChange, rightAction }) => {
  const scrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const syncArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    syncArrows();

    el.addEventListener("scroll", syncArrows, {
      passive: true,
    });

    const ro = new ResizeObserver(syncArrows);
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", syncArrows);
      ro.disconnect();
    };
  }, [documents, syncArrows]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const active = el.querySelector("[data-active='true']");
    active?.scrollIntoView({
      inline: "nearest",
      block: "nearest",
      behavior: "smooth",
    });
  }, [activeDocument]);

  const nudge = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir * 220,
      behavior: "smooth",
    });
  };

  return (
    <div className="border-b bg-background-secondary rounded-lg">
      <div className="flex items-center py-2 gap-2">
        {/* Left Arrow */}

        <div className={`transition-all ${canScrollLeft ? "w-7 opacity-100" : "w-0 opacity-0 overflow-hidden"}`}>
          <Button onClick={() => nudge(-1)} className="h-7 w-7 p-0 rounded-md flex items-center justify-center hover:bg-background-primary">
            <ChevronLeft size={18} />
          </Button>
        </div>

        {/* Tabs */}

        <div ref={scrollRef} className="flex overflow-x-auto custom-scrollbar gap-2 flex-1">
          {documents.map((doc) => {
            const active = activeDocument === doc.id;

            const cfg = STATUS_CONFIG[doc.status] ?? STATUS_CONFIG.NOT_UPLOADED;

            const Icon = cfg.icon;

            return (
              <Button
                key={doc.id}
                data-active={active}
                onClick={() => onChange(doc.id)}
                className={`
    min-w-[180px]
    h-[56px]
    rounded-xl
    border
    px-4
    py-3
    transition-all
    text-left
    justify-start
    ${active ? cfg.active : "border-border-primary bg-background-secondary hover:border-border-secondary"}
  `}
              >
                <div className="flex items-start gap-3 w-full">
                  {/* The icon keeps its status colour even while the tab is active — a
        rejected document must not turn green just because it is selected. */}
                  <Icon size={20} className={cfg.iconClass} />

                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-text-primary truncate">{doc.label}</p>

                    <p className="mt-1 text-xs capitalize text-text-secondary">{(doc.status || "").replace(/_/g, " ").toLowerCase()}</p>
                  </div>
                </div>
              </Button>
            );
          })}
        </div>

        {/* Right Arrow */}

        <div className={`transition-all ${canScrollRight ? "w-7 opacity-100" : "w-0 opacity-0 overflow-hidden"}`}>
          <Button onClick={() => nudge(1)} className="h-7 w-7 p-0 rounded-md flex items-center justify-center hover:bg-background-primary">
            <ChevronRight size={18} />
          </Button>
        </div>

        {rightAction && <div className="ml-2 shrink-0">{rightAction}</div>}
      </div>
    </div>
  );
};
